import type {
  Selection,
  ToteSelection,
  AllUpSelection,
  MysterySelection,
  FobMatchedSelection,
  FobPropositionSelection,
  RaceDetails,
  EventDetails,
  MysteryDetails,
  BetSlipResponse,
  BetSource,
} from '@mobi/betslip/types'
import {
  isAllUpSelection,
  isFobMatchedSelection,
  isFobPropositionSelection,
  isMysterySelection,
  isRaceDetails,
  isSameRaceMultiSelection,
  isStartingPriceSelection,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { store } from '@core/Store'
import { track } from './Analytics'
import {
  keys,
  AnalyticsBetData,
  defaultData,
  MysteryBetData,
  BetConstruction,
} from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { isFavouriteNumbersSelection } from '@core/Data/Betting/selections'
import { isToteSportsDetails, isMysteryDetails } from '@core/Data/Betting/selectionDetails'
import {
  selectIsSkyVideoPlayerOpen,
  selectVideoPlayerChannel,
} from '@core/Areas/SkyVideoPlayer/Store/selectors'
import { FeatureFlagsState } from '@core/State/FeatureFlags/Store'
import { FeatureFlagKeyName } from '@mobi/settings'
import { trackShareBetLinkPlaced } from '@core/Areas/ShareBet/helpers/analytics'
import { trackBetLoopCopyBetPlaced } from '@core/Areas/FollowMe/helpers/analytics'

export const trackBetConfirmation = ({
  ticketNumber,
  selection,
  selectionDetails,
  specialOffers,
  betConstruction,
  betCost,
  winInvestment,
  placeInvestment,
  features,
  bonusCashAmount,
  betSource,
  betSourceShareOrigin,
}: {
  ticketNumber: number
  selection: Selection | null
  selectionDetails: EventDetails | null
  specialOffers: BetSlipResponse['specialOffers']
  betConstruction: BetConstruction
  betCost: number
  winInvestment: number
  placeInvestment: number
  features: FeatureFlagsState
  bonusCashAmount: number
  betSource?: BetSource | null
  betSourceShareOrigin?: string | null
}): void => {
  const isToteBet = isToteSelection(selection)
  const isSportsBet = isToteSportsDetails(selectionDetails)
  const isMysteryBet = isMysteryDetails(selectionDetails)
  const isFavouriteNumbersBet = isFavouriteNumbersSelection(selection)

  let meetingCode: string | null = null
  let meetingName: string | null = null
  const races = (selectionDetails as RaceDetails)?.races
  if (races && races.length) {
    meetingCode = races[0].meetingCode
    meetingName = races[0].meetingName
  }

  const sportsOrRacingData = {
    [isSportsBet ? 'sports' : 'racing']: {
      [isToteBet || isMysteryBet || isFavouriteNumbersBet ? 'tote' : 'fixed']: {
        cost:
          isToteBet || isMysteryBet || isFavouriteNumbersBet
            ? betCost
            : winInvestment + placeInvestment,
        numberOfTickets: 1,
        bonusCashUsed: bonusCashAmount > 0,
        bonusCashAmount,
      },
      info: (selection as ToteSelection | FobMatchedSelection | MysterySelection).fixtureId,
      selection: {
        ...selection, // assist GA by storing all properties of all the different selection Typescript
        betSource,
        raceCode: meetingCode,
        meetingName,
      },
    },
  }

  const betType = getBetType(selection, selectionDetails)
  const betSpecialOffers =
    specialOffers && specialOffers.length > 0 ? specialOffers.map(so => so.title) : null

  let mystery: MysteryBetData | null = null
  if (isMysteryBet) {
    const mysteryDetails = selectionDetails as MysteryDetails
    mystery = {
      isQuickPick: mysteryDetails.betType === 'mystery-quick-pick',
      mysteryType: mysteryDetails.betTypeName,
    }
  }

  const bet: AnalyticsBetData = {
    ...defaultData.bet,
    ...sportsOrRacingData,
    id: ticketNumber,
    betType,
    productType: 'single',
    placementMethod: 'account',
    errorMessage: null,
    construction: betConstruction,
    specialOffers: betSpecialOffers as string[],
    mystery,
  }

  const userFeatures = Object.keys(features)
    .filter(key => features[key as FeatureFlagKeyName] === true)
    .sort()
    .join('|')

  const currentAppState = store.getState()
  const isVisionOpen = selectIsSkyVideoPlayerOpen(currentAppState)
  const visionChannel = selectVideoPlayerChannel(currentAppState)

  track(keys.betAccountProcessed, {
    user: { featureToggles: userFeatures, isVisionOpen, visionChannel },
    bet,
  })

  if (betSourceShareOrigin) {
    const raceCode = meetingCode?.toLowerCase() ?? undefined
    const amountPaid =
      isToteBet || isMysteryBet || isFavouriteNumbersBet ? betCost : winInvestment + placeInvestment
    const hasSpecialOffer = (specialOffers || []).length > 0

    if (betSource === 'bet-loop') {
      trackBetLoopCopyBetPlaced({
        amountPaid,
        hasSpecialOffer,
        legs: 1,
        raceCode,
        origin: betSourceShareOrigin,
      })
    } else {
      trackShareBetLinkPlaced({
        spend: amountPaid,
        has_special: hasSpecialOffer,
        legs: 1,
        raceCode,
        share_origin: betSourceShareOrigin,
      })
    }
  }
}

const getBetType = (
  selection: Selection | null,
  selectionDetails: EventDetails | null
): string | null => {
  if (isSameRaceMultiSelection(selection)) {
    return 'Same Race Multi'
  }

  if (isMysterySelection(selection)) {
    return 'Mystery'
  }

  if (isFavouriteNumbersSelection(selection)) {
    return 'Favourite Numbers'
  }

  if (isToteSelection(selection)) {
    return (selection as ToteSelection).betType.toString()
  }

  if (isFobMatchedSelection(selection)) {
    return 'Win & Place Fixed'
  }

  if (isStartingPriceSelection(selection)) {
    return 'Starting Price'
  }

  if (isAllUpSelection(selection)) {
    return (selection as AllUpSelection).betType.toString()
  }

  if (isFobPropositionSelection(selection)) {
    const marketName = (selection as FobPropositionSelection).marketName
    if (marketName) {
      return marketName
    }

    if (isRaceDetails(selectionDetails)) {
      return 'Race Fixed'
    }
  }

  return null
}
